<template>
  <section
    v-if="showSection"
    id="akcii"
    class="lpu-promotions d-flex flex-column"
    data-qa="lpu_promotions"
  >
    <div class="d-flex align-center justify-space-between">
      <h2
        class="text--text ma-0"
        :class="isMobile ? 'text-h6' : 'text-h5'"
        data-qa="lpu_promotions_title"
      >
        {{ tStatic('Акции') }}
      </h2>
      <button
        v-if="hasArchive"
        type="button"
        class="text-body-1 text-decoration-underline primary--text button-reset flex-shrink-0"
        data-qa="lpu_promotions_archive"
        @click="isArchiveOpen = true"
      >
        {{ tStatic('Архивные') }}
      </button>
    </div>

    <CoreSwiper
      v-if="isMobile && promotions.length"
      :slides-per-view="1"
      :has-pagination="promotions.length > 1"
      :space-between="8"
      :placeholder-min-height="promotionPlaceholderHeight"
      auto-height
    >
      <template #slides="{ slideAttrs }">
        <div
          v-for="promotion in promotions"
          :key="promotion.id"
          v-bind="slideAttrs"
        >
          <PromotionCard
            class="lpu-promotions__item"
            :promotion="promotion"
            :lpu="lpu"
          />
        </div>
      </template>
    </CoreSwiper>

    <div
      v-else-if="promotions.length"
      class="d-flex flex-column"
    >
      <PromotionCard
        v-for="promotion in promotions"
        :key="promotion.id"
        class="lpu-promotions__item"
        :promotion="promotion"
        :lpu="lpu"
      />
    </div>

    <PromotionsArchiveDialog
      v-if="isArchiveOpen"
      :value="isArchiveOpen"
      :archive-url="archiveUrl"
      @input="isArchiveOpen = $event"
    />
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import getLpuPromotionsArchiveUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/getLpuPromotionsArchiveUrl'
import isLpuPromotionsSectionVisible from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/isLpuPromotionsSectionVisible'
import unwrapPromotions from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/unwrapPromotions'
import PromotionCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/components/PromotionCard.vue'
import PromotionsArchiveDialog from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/components/PromotionsArchiveDialog.vue'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const { reverse } = useURLStore()
const isArchiveOpen = ref(false)
const promotions = computed(() => unwrapPromotions(context.promotions))
const hasArchive = computed(() => Boolean(context.has_archive_promotions))
const isStaff = computed(() => Boolean(context.request.user.is_staff))
const archiveUrl = getLpuPromotionsArchiveUrl(reverse, props.lpu.id)
const showSection = computed(() => isLpuPromotionsSectionVisible({
  promotionsLength: promotions.value.length,
  isMobile: isMobile.value,
  isStaff: isStaff.value,
}))

/** Минимум карточки (py-6 + заголовок) + пагинация при нескольких акциях. */
const promotionPlaceholderHeight = computed(() => (
  promotions.value.length > 1 ? 144 : 120
))
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-promotions {
  :deep(.swiper-container > .swiper-pagination-bullets > .swiper-pagination-bullet-active) {
    background-color: var(--lpu-branding-color-primary, $ui-kit-primary);
  }

  &__item {
    border-bottom: 1px solid $ui-kit-bg-gray-60;
  }
}
</style>
